import {
  FormLabel,
  FormControl,
  FormErrorMessage,
  useTheme,
} from '@chakra-ui/react';
import { Control, Controller, FieldError } from 'react-hook-form';
import Select, { OptionTypeBase, Props, StylesConfig } from 'react-select';

export type SelectOption = {
  label: string;
  value: string;
};

interface ISelectProps extends Props<OptionTypeBase, true> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  emptyOptionsMessage?: string;
  name: string;
  label?: string;
  error?: FieldError;
}

export const ReactMultiSelect = ({
  name,
  control,
  emptyOptionsMessage = 'Nenhuma opção disponível',
  label,
  options,
  error,
  isDisabled,
  placeholder = '',
  ...rest
}: ISelectProps): JSX.Element => {
  const theme = useTheme();
  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined;

  const customStyles: StylesConfig<OptionTypeBase, true> = {
    menu: (provided) => ({
      ...provided,
      borderRadius: theme.radii.md,
      padding: 15,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 240,
      overflowY: 'auto',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1500,
    }),
    control: (provided, state) => {
      let shadow = provided.boxShadow;

      if (state.isFocused || state.menuIsOpen) {
        shadow = `0 0 0 1px ${theme.colors.blue[300]}`;
      } else if (error) {
        shadow = `0 0 0 1px ${theme.colors.red[500]}`;
      }

      let border = theme.colors.gray[300];

      if (state.isFocused || state.menuIsOpen) {
        border = `${theme.colors.blue[300]}`;
      } else if (error) {
        border = `${theme.colors.red[500]}`;
      }

      return {
        ...provided,
        backgroundColor: theme.colors.gray[100],
        borderRadius: theme.radii.md,
        borderColor: border,
        boxShadow: shadow,
        fontSize: '18px',
        minHeight: 48,
        padding: '0 4px',
        ':hover': {
          borderColor: border,
        },
      };
    },
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.colors.gray[700],
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
    option: (provided, state: { isSelected: boolean }) => ({
      ...provided,
      borderRadius: theme.radii.md,
      color: state.isSelected ? 'white' : theme.colors.gray[700],
      padding: '10px 20px',
      marginBottom: 4,
      cursor: 'pointer',
    }),
  };

  return (
    <FormControl isInvalid={!!error} isDisabled={isDisabled}>
      {!!label && (
        <FormLabel htmlFor={name} data-disabled={isDisabled ? '' : undefined}>
          {label}
        </FormLabel>
      )}

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <Select
            isMulti
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            options={options}
            styles={customStyles}
            placeholder={placeholder}
            noOptionsMessage={() => emptyOptionsMessage}
            isDisabled={isDisabled}
            menuPortalTarget={menuPortalTarget}
            menuPosition="fixed"
            {...rest}
          />
        )}
      />

      {!!error && (
        <FormErrorMessage position="absolute">{error.message}</FormErrorMessage>
      )}
    </FormControl>
  );
};
